import {
  traducirError,
  type DatosCambioPassword,
  type DatosIngreso,
  type ReclamacionesSesion,
} from '@discopico/domain';
import { leerReclamaciones } from '../../nucleo/autorizacion/sesion.js';
import { obtenerSupabase } from '../../nucleo/supabase/cliente.js';
// `urlFunciones`, `llavePublica` y `codigoDe` vivían acá y se mudaron a un módulo común
// cuando la capa de llamadas de usuarios necesitó las mismas tres. La tercera copia es
// donde una regla empieza a divergir sin que nadie lo note.
import { codigoDe, llavePublica, urlFunciones } from '../../nucleo/supabase/peticiones.js';

/**
 * Error de acceso. Guarda el CÓDIGO del contrato y traduce el mensaje con
 * `traducirError` del paquete de dominio, así que la pantalla no tiene ni un texto de
 * error escrito a mano y todos los mensajes del sistema salen del mismo catálogo.
 *
 * Quien lo muestre tiene que traducir `codigo`, no leer `message`: el catálogo dice que
 * el código es el contrato y el mensaje no lo es.
 */
export class ErrorAcceso extends Error {
  constructor(public readonly codigo: string) {
    super(traducirError(codigo));
    this.name = 'ErrorAcceso';
  }
}

/**
 * Ingreso por documento. Llama a la Edge Function, instala la sesión que devuelve y
 * responde con las reclamaciones que venían dentro del token.
 *
 * No se llama a `signInWithPassword` desde el navegador: eso exigiría el correo
 * sintético, y derivarlo acá obligaría a publicar el algoritmo y convertiría la pantalla
 * en un verificador de documentos.
 */
export async function ingresar(datos: DatosIngreso): Promise<ReclamacionesSesion | null> {
  let respuesta: Response;
  try {
    respuesta = await fetch(`${urlFunciones()}/ingresar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: llavePublica() },
      body: JSON.stringify({
        tipo_documento: datos.tipoDocumento,
        numero_documento: datos.numeroDocumento,
        password: datos.password,
      }),
    });
  } catch {
    // Sin red no es un problema de credenciales, y decirle "clave incorrecta" a alguien
    // que tiene el wifi caído lo hace dudar de su memoria en vez de mirar el router.
    throw new ErrorAcceso('E_SIN_CONEXION');
  }

  const cuerpo = (await respuesta.json().catch(() => ({}))) as Record<string, unknown>;

  if (!respuesta.ok) {
    throw new ErrorAcceso(
      typeof cuerpo.codigo === 'string' ? cuerpo.codigo : 'E_CREDENCIALES_INVALIDAS',
    );
  }

  const { error } = await obtenerSupabase().auth.setSession({
    access_token: String(cuerpo.access_token ?? ''),
    refresh_token: String(cuerpo.refresh_token ?? ''),
  });
  if (error) throw new ErrorAcceso('E_CREDENCIALES_INVALIDAS');

  return leerReclamaciones(String(cuerpo.access_token ?? ''));
}

/**
 * Cambio de contraseña en dos pasos, y los dos son necesarios.
 *
 * `updateUser` cambia la credencial en Auth, que es el único que puede hacerlo.
 * `confirmar_cambio_password` baja la bandera en `public.usuario`, que es lo que
 * `app.assert_puede_operar` mira antes de cada escritura.
 *
 * Y el tercer paso, que es el que se olvida: `refreshSession`. El token vigente todavía
 * dice `debe_cambiar_password: true`, porque se emitió antes del cambio. Sin refrescarlo,
 * el guardián de rutas sigue mandando al usuario a la pantalla de cambio de contraseña
 * durante los treinta minutos que le quedan de vida al token, y el usuario ve un
 * formulario que acaba de completar con éxito.
 */
export async function cambiarPassword(datos: DatosCambioPassword): Promise<void> {
  const { error: errorAuth } = await obtenerSupabase().auth.updateUser({ password: datos.password });
  if (errorAuth) throw new ErrorAcceso('E_PASSWORD_INVALIDA');

  const { error: errorRpc } = await obtenerSupabase().rpc('rpc_confirmar_cambio_password');
  if (errorRpc) throw new ErrorAcceso(codigoDe(errorRpc.message));

  await obtenerSupabase().auth.refreshSession();
}

export async function salir(): Promise<void> {
  await obtenerSupabase().auth.signOut();
}
